import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { compressImage, PHOTO_SIZES, type PhotoKind } from '@/lib/image'
import { discardMedia, getMediaBlob, saveMedia } from '@/offline/media'
import { CAMERA_PROBLEM, cameraProblem, type CameraProblem } from './cameraProblem'
import { Btn, Cta, V1Sheet, WaitCta } from './kit'
import { Refused } from './scan'
import { C } from './style'
import { tr } from './text'

/**
 * Photo capture for the V1 screens (slip, page photo): the map's "in-app photo" — a live camera
 * with a shutter, the same on a phone and on a laptop webcam — then compressed to JPEG, kept in
 * IndexedDB and uploaded later by sync (same storage as the app's PhotoCapture). फ़ोन से चुनें
 * picks a saved picture when there is no camera. `render` draws the board's row; `take()` opens
 * the camera.
 */
export function V1Photo({
  value,
  onChange,
  render,
  open: openProp,
  onOpenChange,
  kind = 'proof',
}: {
  /** How big the photo is kept (see PHOTO_SIZES): a reading needs far less than a face. */
  kind?: PhotoKind
  value: string | null
  onChange: (mediaId: string | null) => void
  /** Optional: open the camera from elsewhere on the screen. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  render: (p: {
    preview: string | null
    busy: boolean
    failed: boolean
    take: () => void
  }) => ReactNode
}) {
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  const [openState, setOpenState] = useState(false)
  const open = openProp ?? openState
  const setOpen = useCallback(
    (v: boolean) => (onOpenChange ? onOpenChange(v) : setOpenState(v)),
    [onOpenChange],
  )

  useEffect(() => {
    if (!value) return
    let url: string | null = null
    let alive = true
    void getMediaBlob(value).then((blob) => {
      if (!alive || !blob) return
      url = URL.createObjectURL(blob)
      setPreview(url)
    })
    return () => {
      alive = false
      if (url) URL.revokeObjectURL(url)
      setPreview(null)
    }
  }, [value])

  const store = async (blob: Blob) => {
    setBusy(true)
    setFailed(false)
    try {
      const id = await saveMedia(await compressImage(blob, PHOTO_SIZES[kind]))
      if (value) await discardMedia(value)
      onChange(id)
      setOpen(false)
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  const take = useCallback(() => setOpen(true), [setOpen])
  return (
    <>
      {render({ preview, busy, failed, take })}
      {open && (
        <CameraSheet busy={busy} onClose={() => setOpen(false)} onShot={(b) => void store(b)} />
      )}
    </>
  )
}

function CameraSheet({
  busy,
  onClose,
  onShot,
}: {
  busy: boolean
  onClose: () => void
  onShot: (b: Blob) => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [file, setFile] = useState<HTMLInputElement | null>(null)
  const [problem, setProblem] = useState<CameraProblem | null>(null)
  const [ready, setReady] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const stream = useRef<MediaStream | null>(null)

  useEffect(() => {
    // The video is mounted whenever there is no problem; a retry clears the problem and remounts it.
    const video = videoRef.current
    if (!video) return
    let alive = true
    void (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setProblem('insecure')
        return
      }
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } },
          audio: false,
        })
        if (!alive) {
          s.getTracks().forEach((t) => t.stop())
          return
        }
        stream.current = s
        video.srcObject = s
        await video.play()
        setReady(true)
      } catch (e) {
        if (alive) setProblem(cameraProblem(e))
      }
    })()
    return () => {
      alive = false
      stream.current?.getTracks().forEach((t) => t.stop())
      stream.current = null
      setReady(false)
    }
  }, [attempt])

  const shoot = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    const c = document.createElement('canvas')
    c.width = video.videoWidth
    c.height = video.videoHeight
    c.getContext('2d')?.drawImage(video, 0, 0)
    c.toBlob((b) => b && onShot(b), 'image/jpeg', 0.9)
  }

  return (
    <V1Sheet
      title={tr('gate.v1.photo.title')}
      onClose={onClose}
      top={40}
      actions={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ready ? (
            <Cta label={tr('gate.v1.photo.shoot')} icon="camera" busy={busy} onClick={shoot} />
          ) : (
            <WaitCta>{busy ? tr('gate.v1.photo.saving') : tr('gate.v1.photo.title')}</WaitCta>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {problem && problem !== 'insecure' && (
              <Btn
                icon="camera"
                onClick={() => {
                  setProblem(null)
                  setAttempt((n) => n + 1)
                }}
              >
                {tr('gate.v1.camera.retry')}
              </Btn>
            )}
            <Btn icon="book" onClick={() => file?.click()}>
              {tr('gate.v1.photo.gallery')}
            </Btn>
          </div>
        </div>
      }
    >
      <input
        ref={setFile}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onShot(f)
          e.target.value = ''
        }}
      />
      <div
        style={{
          flexGrow: 1,
          minHeight: 280,
          borderRadius: 14,
          background: C.scanner,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {!problem && (
          <video
            ref={videoRef}
            muted
            playsInline
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}
      </div>
      {problem && <Refused text={tr(CAMERA_PROBLEM[problem])} />}
    </V1Sheet>
  )
}
